/**
 * Logo List — infinite auto-scroll
 *
 * Used by: sections/index-logo-list.liquid
 *
 * Marquee runs only when BOTH are true:
 *   1. Viewport width < data-marquee-max-width (default 1300px)
 *   2. Logo track is wider than the visible viewport
 *
 * At >= 1300px logos stay static and centered (no transform).
 *
 * Data attributes on the section element:
 *   - data-index-logo-list      — init hook
 *   - data-scroll-duration      — full cycle time in seconds (default 30)
 *   - data-marquee-max-width    — max viewport width for marquee (default 1300)
 */
(function () {
  const initLogoList = (section) => {
    // Prevent double-init in theme editor / section reloads
    if (!section || section.dataset.logoListInitialized === 'true') return;
    section.dataset.logoListInitialized = 'true';

    const viewport = section.querySelector('.lp-index-logo-list__viewport');
    const track = section.querySelector('.lp-index-logo-list__track');
    if (!viewport || !track) return;

    let rafId = null;
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    let onResize = null;
    let onMouseEnter = null;
    let onMouseLeave = null;
    let measure = null;

    // Tear down listeners, animation, clones, and transform on section unload
    const cleanup = () => {
      if (rafId !== null) {
        cancelAnimationFrame(rafId);
        rafId = null;
      }

      window.removeEventListener('resize', onResize);
      if (onMouseEnter) viewport.removeEventListener('mouseenter', onMouseEnter);
      if (onMouseLeave) viewport.removeEventListener('mouseleave', onMouseLeave);

      section.classList.remove('lp-index-logo-list--marquee');
      track.style.transform = '';
      delete section.dataset.logoListInitialized;
      delete section._logoListCleanup;
    };

    section._logoListCleanup = cleanup;

    // Accessibility: keep static layout when user prefers reduced motion
    if (reducedMotion) {
      track.style.transform = '';
      return;
    }

    const duration = (Number(section.dataset.scrollDuration) || 30) * 1000;
    const marqueeMaxWidth = Number(section.dataset.marqueeMaxWidth) || 1300;
    let offset = 0;
    let paused = false;
    let lastTime = null;
    let originalWidth = 0;
    let speed = 0;
    let useClone = false;

    const getTrackGap = () => {
      const gapValue = parseFloat(getComputedStyle(track).gap);
      return Number.isFinite(gapValue) ? gapValue : 0;
    };

    // Original logos only — excludes clones appended for seamless looping
    const getOriginalItems = () =>
      [...track.querySelectorAll('.lp-index-logo-list__item:not([data-logo-clone])')];

    // Total width of one full logo set (used as the scroll loop distance)
    const measureOriginalWidth = () => {
      const items = getOriginalItems();
      if (items.length === 0) return 0;

      const gap = getTrackGap();
      let width = 0;

      items.forEach((item, index) => {
        width += item.offsetWidth;
        if (index > 0) width += gap;
      });

      return width;
    };

    const applyOffset = () => {
      track.style.transform = `translate3d(${offset}px, 0, 0)`;
    };

    // Keep offset in a valid range after resize or loop wrap
    const normalizeOffset = () => {
      if (originalWidth <= 0) {
        offset = 0;
        return;
      }

      if (useClone) {
        while (offset <= -originalWidth) offset += originalWidth;
        while (offset > 0) offset -= originalWidth;
      } else {
        if (offset < -originalWidth) offset = -originalWidth;
        if (offset > 0) offset = 0;
      }
    };

    // Duplicate logos once for seamless infinite scroll
    const ensureClones = () => {
      if (track.dataset.cloned === 'true') return;

      getOriginalItems().forEach((item) => {
        const clone = item.cloneNode(true);
        clone.setAttribute('data-logo-clone', 'true');
        clone.setAttribute('aria-hidden', 'true');
        if (clone.tagName === 'A') clone.tabIndex = -1;
        track.appendChild(clone);
      });

      track.dataset.cloned = 'true';
    };

    const removeClones = () => {
      track.querySelectorAll('[data-logo-clone]').forEach((clone) => clone.remove());
      track.dataset.cloned = 'false';
    };

    /**
     * Decide static vs marquee mode and recalculate scroll speed.
     * Called on init, resize, and after images finish loading.
     *
     * When leaving marquee (e.g. 511 → 1440), offset/transform must reset.
     * Otherwise a leftover translate leaves logos stuck left with empty space.
     */
    measure = ({ preserveOffset = false } = {}) => {
      const previousWidth = originalWidth;
      const previousOffset = offset;
      const wasMarquee = useClone;

      originalWidth = measureOriginalWidth();

      if (originalWidth <= 0) {
        speed = 0;
        useClone = false;
        offset = 0;
        section.classList.remove('lp-index-logo-list--marquee');
        track.style.transform = '';
        return;
      }

      const isMarqueeViewport = window.innerWidth < marqueeMaxWidth;
      const nextUseClone = isMarqueeViewport && originalWidth > viewport.clientWidth;

      if (nextUseClone) {
        ensureClones();
        section.classList.add('lp-index-logo-list--marquee');
      } else {
        removeClones();
        section.classList.remove('lp-index-logo-list--marquee');
      }

      useClone = nextUseClone;
      speed = nextUseClone ? originalWidth / duration : 0;

      // Static desktop layout: always clear transform so justify-content: center works
      if (!nextUseClone) {
        offset = 0;
        track.style.transform = '';
        return;
      }

      // Still in marquee: preserve scroll position across resize when possible
      if (preserveOffset && wasMarquee && previousWidth > 0) {
        offset = (previousOffset / previousWidth) * originalWidth;
        normalizeOffset();
      } else if (!preserveOffset || !wasMarquee) {
        offset = 0;
      }

      applyOffset();
    };

    measure();

    // Pause scroll on hover
    onMouseEnter = () => {
      paused = true;
    };

    onMouseLeave = () => {
      paused = false;
      lastTime = null;
    };

    onResize = () => {
      measure({ preserveOffset: true });
    };

    window.addEventListener('resize', onResize);
    viewport.addEventListener('mouseenter', onMouseEnter);
    viewport.addEventListener('mouseleave', onMouseLeave);

    // Re-measure once lazy-loaded logo images have dimensions
    track.querySelectorAll('img').forEach((image) => {
      if (!image.complete) {
        image.addEventListener('load', () => measure({ preserveOffset: true }), { once: true });
      }
    });

    // requestAnimationFrame loop — moves track left via translate3d
    const tick = (time) => {
      if (speed > 0 && lastTime !== null && !paused) {
        const delta = time - lastTime;
        offset -= speed * delta;

        if (useClone) {
          if (offset <= -originalWidth) offset += originalWidth;
        } else if (offset <= -originalWidth) {
          offset = 0;
        }

        applyOffset();
      }

      lastTime = time;
      rafId = requestAnimationFrame(tick);
    };

    rafId = requestAnimationFrame(tick);
  };

  // Init all logo list sections on page load
  document.querySelectorAll('[data-index-logo-list]').forEach(initLogoList);

  // Theme editor: init when section is added/re-rendered
  document.addEventListener('shopify:section:load', (event) => {
    const section = event.target.querySelector('[data-index-logo-list]');
    if (section) initLogoList(section);
  });

  // Theme editor: cleanup when section is removed
  document.addEventListener('shopify:section:unload', (event) => {
    event.target.querySelector('[data-index-logo-list]')?._logoListCleanup?.();
  });
})();
